import { useState } from 'react'
import { Link } from 'react-router'
import PropTypes from 'prop-types'

import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined'
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined'
import HomeIcon from '@mui/icons-material/Home'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchOffIcon from '@mui/icons-material/SearchOff'

import Button from '@mui/material/Button'

import useFormatMessage from '@hooks/useFormatMessage'
import config from '@config'

import ErrorLayout, { ErrorHero } from './ErrorBase.components'
import ErrorDialog from './ErrorDialog'

ErrorBase.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  code: PropTypes.oneOf([401, 404, 500]).isRequired,
  error: PropTypes.object,
  reset: PropTypes.func,
}

const icons = {
  401: LockOutlinedIcon,
  404: SearchOffIcon,
  500: BuildOutlinedIcon,
}

export default function ErrorBase({ title, subtitle, code, error, reset = () => undefined }) {
  const formatMessage = useFormatMessage()
  const [open, setOpen] = useState(false)

  const isNative = window.isNativeApp === true
  const retryFirst = code === 500 || isNative

  const retry = (
    <Button
      key="retry"
      size="large"
      variant={retryFirst ? 'contained' : 'text'}
      onClick={() => window.location.reload()}
      startIcon={<RefreshIcon aria-hidden="true" />}
      data-testid="error-try-again"
    >
      {formatMessage('error.page.tryAgain')}
    </Button>
  )

  const home = (
    <Button
      key="home"
      size="large"
      variant={retryFirst ? 'text' : 'contained'}
      component={Link}
      to={config.defaultPath}
      onClick={reset}
      startIcon={<HomeIcon aria-hidden="true" />}
      aria-label={formatMessage('navigation.to.a11y', { label: formatMessage('navigation.home') })}
    >
      {formatMessage('navigation.home')}
    </Button>
  )

  const support = (
    <Button
      key="support"
      size="large"
      variant="text"
      component={Link}
      to="/app/messages/support"
      onClick={reset}
      startIcon={<ForumOutlinedIcon aria-hidden="true" />}
      data-testid="error-message-support"
    >
      {formatMessage('error.page.messageSupport')}
    </Button>
  )

  return (
    <ErrorLayout
      hero={<ErrorHero icon={icons[code]} />}
      caption={formatMessage('error.page.a11y.errorCode', { code })}
      title={title}
      subtitle={subtitle}
      primary={retryFirst ? retry : home}
      secondary={isNative ? null : retryFirst ? [home, support] : [retry, support]}
      footer={
        error && (
          <>
            <Button size="small" variant="text" color="inherit" onClick={() => setOpen(true)} sx={{ color: 'text.secondary' }}>
              {formatMessage('error.page.viewError')}
            </Button>
            <ErrorDialog error={error} open={open} onClose={() => setOpen(false)} />
          </>
        )
      }
    />
  )
}
